import { useTranslation } from 'react-i18next';
import type { ActivityBlockData } from '@foco/contracts';

/**
 * Declared activities (CNAE in Brazil).
 *
 * The primary code is shown with its official description verbatim, because the
 * description is what makes the code meaningful: "6201-5/01" means nothing to a
 * buyer, "Desenvolvimento de programas de computador sob encomenda" does. The
 * registry writes it in the country's language, so it is shown untranslated —
 * a translated activity name would no longer match the official document.
 */
export function ActivityBlock({ data }: { data: unknown }): React.ReactNode {
  const { t } = useTranslation();
  const block = (data ?? {}) as ActivityBlockData;

  return (
    <div className="space-y-4">
      {block.primary && (
        <div>
          <p className="text-xs text-[var(--color-muted-fg)]">{t('blocks.activity.primary')}</p>
          <p className="mt-0.5 text-sm">
            <span className="font-mono text-[13px] text-[var(--color-muted-fg)]">
              {block.primary.code}
            </span>{' '}
            {block.primary.name}
          </p>
        </div>
      )}

      {block.secondary?.length > 0 && (
        <div>
          <p className="text-xs text-[var(--color-muted-fg)]">
            {t('blocks.activity.secondary')} ({block.secondary.length})
          </p>
          <ul className="mt-1.5 space-y-1 text-sm">
            {block.secondary.slice(0, 12).map((activity) => (
              <li key={activity.code}>
                <span className="font-mono text-[13px] text-[var(--color-muted-fg)]">
                  {activity.code}
                </span>{' '}
                {activity.name}
              </li>
            ))}
          </ul>
          {block.secondary.length > 12 && (
            <p className="mt-1 text-xs text-[var(--color-muted-fg)]">
              {t('blocks.activity.more', { count: block.secondary.length - 12 })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
